import { isLocale, type Locale } from './config';

/**
 * Where a locale preference is kept.
 *
 * A port, not a `localStorage` call inlined into the provider. Today the only
 * adapter is browser storage; P0-WEB-08 adds an authenticated one that writes
 * through to `identity_users.locale`, and the landing site's prerender needs an
 * adapter that does nothing at all because there is no `window`. Each of those
 * is a different implementation of these two methods, not a different provider.
 */
export interface LocalePreferenceStore {
  read(): Locale | undefined;
  write(locale: Locale): void;
}

export const LOCALE_STORAGE_KEY = 'planetra.locale';

/**
 * No-op store. The default during a static prerender, and the safe fallback
 * anywhere `window` is absent.
 */
export const nullLocaleStore: LocalePreferenceStore = {
  read: () => undefined,
  write: () => undefined,
};

/**
 * Browser storage.
 *
 * Every access is guarded: Safari in private browsing and any page under a
 * blocked-cookies policy throw on `localStorage` access rather than returning
 * null. A language preference is not worth taking the whole app down for, so a
 * failure here degrades to "no stored preference".
 */
export function createLocalStorageStore(
  storage: Storage | undefined = typeof window === 'undefined' ? undefined : window.localStorage,
): LocalePreferenceStore {
  if (storage === undefined) return nullLocaleStore;

  return {
    read: () => {
      try {
        const stored = storage.getItem(LOCALE_STORAGE_KEY);
        return isLocale(stored) ? stored : undefined;
      } catch {
        return undefined;
      }
    },
    write: (locale) => {
      try {
        storage.setItem(LOCALE_STORAGE_KEY, locale);
      } catch {
        // Preference not persisted. The session still switches.
      }
    },
  };
}
